import React, { useMemo } from 'react';
import styles from './Preview.module.css';
import cx from 'classnames';
import { useOnboardingContext } from '../../../contexts/OnboardingContext';
import { api } from '../../../services/ApiService';
import PdfDownload from './PdfDownload';

interface PreviewStepProps {
    onSubmit: () => void;
    isActive?: boolean;
}

const PreviewStep: React.FC<PreviewStepProps> = ({isActive = false }) => {
    const { formData } = useOnboardingContext();

    const formatPrice = (price: number): string => {
        if (price === 0) {
            return 'kostenlos';
        }
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(price);
    };

    const placeholderImageUrl = useMemo(() => {
        const apiUrl = api.getBaseUrl();
        return `${apiUrl}storage/placeholder/tsoutfit.png`;
    }, []);

    return (
        <div className={cx(styles['preview-step-container'])} hidden={!isActive}>
            <h2 className={styles['step-title']}>Vorschau</h2>
            <p className={styles['step-description']}>
                Überprüfen Sie alle Einstellungen bevor Sie fortfahren.
            </p>

            <div className={styles['preview-layout']}>
                <section className={styles['preview-section']}>
                        <h3 className={styles['section-title']}>Benutzerdaten & CI</h3>
                        <div className={styles['section-body']}>
                            {formData.selectedTypes.length > 0 && (
                                <div className={styles['block']}>
                                    <div className={styles['block-label']}>Branche</div>
                                    <div className={styles['types-row']}>
                                        {formData.selectedTypes.map((type) => (
                                            <span key={type.id} className={styles['type-tag']}>
                                                {type.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {formData.userData.customTypeName && (
                                <div className={styles['data-row']}>
                                    <span className={styles['data-label']}>Andere Branche</span>
                                    <span className={styles['data-value']}>{formData.userData.customTypeName}</span>
                                </div>
                            )}
                            <div className={styles['data-rows']}>
                                {formData.userData.company && (
                                    <div className={styles['data-row']}>
                                        <span className={styles['data-label']}>Firmenname</span>
                                        <span className={styles['data-value']}>{formData.userData.company}</span>
                                    </div>
                                )}
                                {formData.userData.firstName && (
                                    <div className={styles['data-row']}>
                                        <span className={styles['data-label']}>Vorname</span>
                                        <span className={styles['data-value']}>{formData.userData.firstName}</span>
                                    </div>
                                )}
                                {formData.userData.lastName && (
                                    <div className={styles['data-row']}>
                                        <span className={styles['data-label']}>Nachname</span>
                                        <span className={styles['data-value']}>{formData.userData.lastName}</span>
                                    </div>
                                )}
                                {(formData.userData.street || formData.userData.houseNumber) && (
                                    <div className={styles['data-row']}>
                                        <span className={styles['data-label']}>Straße</span>
                                        <span className={styles['data-value']}>
                                            {[
                                                formData.userData.street,
                                                formData.userData.houseNumber,
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                        </span>
                                    </div>
                                )}
                                {formData.userData.postalCode && (
                                    <div className={styles['data-row']}>
                                        <span className={styles['data-label']}>PLZ</span>
                                        <span className={styles['data-value']}>{formData.userData.postalCode}</span>
                                    </div>
                                )}
                                {formData.userData.city && (
                                    <div className={styles['data-row']}>
                                        <span className={styles['data-label']}>Ort</span>
                                        <span className={styles['data-value']}>{formData.userData.city}</span>
                                    </div>
                                )}
                                {formData.userData.email && (
                                    <div className={styles['data-row']}>
                                        <span className={styles['data-label']}>E-Mail</span>
                                        <span className={styles['data-value']}>{formData.userData.email}</span>
                                    </div>
                                )}
                                {formData.userData.phoneNumber && (
                                    <div className={styles['data-row']}>
                                        <span className={styles['data-label']}>Telefon</span>
                                        <span className={styles['data-value']}>{formData.userData.phoneNumber}</span>
                                    </div>
                                )}
                                {formData.userData.memberCount && (
                                    <div className={styles['data-row']}>
                                        <span className={styles['data-label']}>Mitarbeiteranzahl</span>
                                        <span className={styles['data-value']}>{formData.userData.memberCount}</span>
                                    </div>
                                )}
                                {formData.userData.additionalInformation && (
                                    <div className={cx(styles['data-row'], styles['data-row-full'])}>
                                        <span className={styles['data-label']}>Zusätzliche Infos</span>
                                        <span className={styles['data-value']}>{formData.userData.additionalInformation}</span>
                                    </div>
                                )}
                            </div>
                            <div className={styles['block']}>
                                <div className={styles['block-label']}>Firmenfarben & CI</div>
                                <div className={styles['ci-row']}>
                                    <div className={styles['ci-item']}>
                                        <div
                                            className={styles['ci-swatch']}
                                            style={{ backgroundColor: formData.primaryColor || '#f0f0f0' }}
                                        />
                                        <span className={styles['ci-label']}>Primär</span>
                                    </div>
                                    <div className={styles['ci-item']}>
                                        <div
                                            className={styles['ci-swatch']}
                                            style={{ backgroundColor: formData.secondaryColor || '#e0e0e0' }}
                                        />
                                        <span className={styles['ci-label']}>Sekundär</span>
                                    </div>
                                </div>
                                {(formData.font || formData.style) && (
                                    <div className={styles['ci-meta']}>
                                        {formData.font && <span>Schrift: {formData.font}</span>}
                                        {formData.font && formData.style && ' · '}
                                        {formData.style && <span>Stil: {formData.style}</span>}
                                    </div>
                                )}
                                {formData.logos.filter((l) => l.file !== null).length > 0 && (
                                    <div className={styles['logos-row']}>
                                        {formData.logos
                                            .filter((l) => l.file !== null)
                                            .map((logo) => (
                                                <div key={logo.id} className={styles['logo-thumb']}>
                                                    {logo.preview && (
                                                        <img
                                                            src={logo.preview}
                                                            alt={logo.label || 'Logo'}
                                                            className={styles['logo-img']}
                                                        />
                                                    )}
                                                    <span className={styles['logo-name']}>{logo.label || logo.file?.name || 'Logo'}</span>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>

                {formData.selectedArticles.length > 0 && (
                    <section className={styles['preview-section']}>
                        <h3 className={styles['section-title']}>Ausgewählte Artikel ({formData.selectedArticles.length})</h3>
                        <div className={styles['section-body']}>
                            <div className={styles['articles-list']}>
                                {formData.selectedArticles.map((article) => (
                                    <div key={article.id} className={styles['article-row']}>
                                        <div className={styles['article-thumb']}>
                                            <img
                                                src={article.imageUrl || placeholderImageUrl}
                                                alt={article.name}
                                                className={styles['article-img']}
                                            />
                                        </div>
                                        <div className={styles['article-main']}>
                                            <span className={styles['article-name']}>{article.name}</span>
                                            <span className={styles['article-price']}>{formatPrice(article.price)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </div>

            <PdfDownload />
        </div>
    );
};

export default PreviewStep;

